import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, TrendingUp, TrendingDown, Building2, CreditCard, ArrowUpRight, ArrowDownLeft, RefreshCw, ChevronRight, Activity } from 'lucide-react';
import axios from 'axios';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { useWallets, useBankAccount } from '../hooks/useData';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chart data
const chartData = [
  { date: 'Mon', value: 14500 },
  { date: 'Tue', value: 15200 },
  { date: 'Wed', value: 14800 },
  { date: 'Thu', value: 16100 },
  { date: 'Fri', value: 15800 },
  { date: 'Sat', value: 16500 },
  { date: 'Sun', value: 17200 },
];

const CRYPTO_CONFIG = {
  BTC: { color: '#F7931A', name: 'Bitcoin' },
  ETH: { color: '#627EEA', name: 'Ethereum' },
  SOL: { color: '#9945FF', name: 'Solana' },
  MATIC: { color: '#8247E5', name: 'Polygon' },
  BNB: { color: '#F0B90B', name: 'BNB' },
};

const Overview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { wallets } = useWallets();
  const { bankAccount } = useBankAccount(user?.email);
  const [livePrices, setLivePrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(true);

  const cardBg = isDark ? 'bg-black/40' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await axios.get(`${API}/trading/prices/live`);
        const pricesArray = response.data.prices || [];
        const pricesObj = {};
        pricesArray.forEach(coin => {
          pricesObj[coin.symbol] = coin;
        });
        setLivePrices(pricesObj);
      } catch (error) {
        console.error('Error fetching prices:', error);
      } finally {
        setPricesLoading(false);
      }
    };
    
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, []);

  const fiatBalance = bankAccount?.balance || 0;
  
  const cryptoValue = wallets.reduce((sum, wallet) => {
    const symbol = wallet.network?.toUpperCase() === 'ETHEREUM' ? 'ETH' 
      : wallet.network?.toUpperCase() === 'BITCOIN' ? 'BTC'
      : wallet.network?.toUpperCase() === 'SOLANA' ? 'SOL'
      : wallet.network?.toUpperCase() === 'POLYGON' ? 'MATIC'
      : wallet.network?.toUpperCase() === 'BSC' ? 'BNB'
      : wallet.network?.toUpperCase();
    const price = livePrices[symbol]?.price || 0;
    const balance = wallet.balance || 0;
    return sum + (price * balance);
  }, 0);

  const totalValue = fiatBalance + cryptoValue;

  const formatPrice = (price) => {
    if (!price && price !== 0) return '$0.00';
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div data-testid="overview-page" className="min-h-screen py-6 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-2xl md:text-3xl font-bold ${textPrimary} mb-1`}>Overview</h1>
          <p className={textMuted}>Your complete financial snapshot</p>
        </div>

        {/* Total Value Card */}
        <div className={`${cardBg} backdrop-blur-xl border ${cardBorder} rounded-2xl p-6 mb-6`}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Total Value */}
            <div className="lg:col-span-2">
              <div className={`text-sm ${textMuted} mb-2`}>Total Balance</div>
              <div className={`text-4xl font-mono font-bold ${textPrimary} mb-4`}>
                {formatPrice(totalValue)}
              </div>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <Building2 className="w-5 h-5 text-purple-500" />
                  <div>
                    <div className={`text-xs ${textMuted}`}>Fiat Balance</div>
                    <div className={`font-mono font-semibold ${textPrimary}`}>€{fiatBalance.toLocaleString()}</div>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <Wallet className="w-5 h-5 text-orange-500" />
                  <div>
                    <div className={`text-xs ${textMuted}`}>Crypto Value</div>
                    <div className={`font-mono font-semibold ${textPrimary}`}>{formatPrice(cryptoValue)}</div>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" stroke="rgba(255,255,255,0.2)" style={{ fontSize: '12px' }} />
                    <YAxis stroke="rgba(255,255,255,0.2)" style={{ fontSize: '12px' }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Value']}
                    />
                    <Area type="monotone" dataKey="value" stroke="#a855f7" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-3">
              <h3 className={`font-semibold ${textPrimary} mb-4`}>Quick Actions</h3>
              
              <button
                onClick={() => navigate('/portfolio')}
                className={`w-full flex items-center justify-between p-4 ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'} rounded-xl transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-purple-500" />
                  </div>
                  <div className="text-left">
                    <div className={`font-medium ${textPrimary}`}>Banking</div>
                    <div className={`text-xs ${textMuted}`}>Cards & Account</div>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 ${textMuted}`} />
              </button>

              <button
                onClick={() => navigate('/wallets')}
                className={`w-full flex items-center justify-between p-4 ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'} rounded-xl transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-orange-500" />
                  </div>
                  <div className="text-left">
                    <div className={`font-medium ${textPrimary}`}>Crypto Wallets</div>
                    <div className={`text-xs ${textMuted}`}>Manage assets</div>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 ${textMuted}`} />
              </button>

              <button
                onClick={() => navigate('/swap')}
                className={`w-full flex items-center justify-between p-4 ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'} rounded-xl transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="text-left">
                    <div className={`font-medium ${textPrimary}`}>OTC Exchange</div>
                    <div className={`text-xs ${textMuted}`}>Swap fiat & crypto</div>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 ${textMuted}`} />
              </button>

              <button
                onClick={() => navigate('/transactions')}
                className={`w-full flex items-center justify-between p-4 ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'} rounded-xl transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-left">
                    <div className={`font-medium ${textPrimary}`}>Transactions</div>
                    <div className={`text-xs ${textMuted}`}>View history</div>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 ${textMuted}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Live Market Prices */}
        <div className={`${cardBg} backdrop-blur-xl border ${cardBorder} rounded-2xl p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-semibold ${textPrimary}`}>Live Market Prices</h3>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className={`text-xs ${textMuted}`}>CoinGecko</span>
            </div>
          </div>
          
          {pricesLoading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 text-purple-500 mx-auto animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {Object.entries(livePrices).slice(0, 6).map(([symbol, data]) => (
                <div
                  key={symbol}
                  className={`p-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-xl`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ 
                        backgroundColor: (CRYPTO_CONFIG[symbol]?.color || '#6B7280') + '30',
                        color: CRYPTO_CONFIG[symbol]?.color || '#6B7280'
                      }}
                    >
                      {symbol.slice(0, 3)}
                    </div>
                    <span className={`font-medium ${textPrimary}`}>{symbol}</span>
                  </div>
                  <div className={`font-mono text-lg ${textPrimary}`}>{formatPrice(data.price)}</div>
                  <div className={`text-sm flex items-center gap-1 ${data.change_24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {data.change_24h >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {data.change_24h >= 0 ? '+' : ''}{data.change_24h?.toFixed(2)}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Overview;
